import React, { useEffect, useRef, useState } from "react";
import { BackButton } from "../../components/btn/Buttons";
import PageHeading from "../../components/PageHeading";
import { Input } from "../../components/inputs/Inputs";
import Button from "../../components/btn/Button";
import { useSelector } from "react-redux";
import Table from "../../components/table/Table";
import { get_deleted_report_list } from "./DeletedReportSlice";
import moment from "moment";
import Loader from "../../components/Loader";
import { Pagination2 } from "../../components/pagination/pagination";
import NoData from "../../components/NoData";
import SmallModal from "../../components/modal/SmallModal";
import { base_url, downloadExcel, exportPDF } from "../../utils/utils";
import { Alerts } from "../../components/Alerts/Alerts";

const DeleteReport = () => {
  const [addData, setAddData] = useState({
    from_date: "",
    to_date: "",
  });
  const [addDataPre, setAddDataPre] = useState({});
  const [showLoader, setShowLoader] = useState(false);
  const [open, setOpen] = useState(false);
  const [mess, setMess] = useState("");
  const [totalCount, setTotalCount] = useState("50");
  const [searchCount, setSearchCount] = useState(null); // searchcount from API when search is active
  const [entries, setEntries] = useState("10");
  const [pageNo, setPageNo] = useState(1);
  const [serchInput, setSerchInput] = useState("");
  const [showData, setShowData] = useState(false);
  const [deleted_report_item_list, setDeletedReportItemList] = useState([]);

  // ── Refs ──────────────────────────────────────────────────────────────────
  // searchRef  : always holds the latest search string so get_list() closures
  //              never read a stale value.
  // debounceRef: timer handle so rapid keystrokes cancel the previous call.
  // pageNoRef  : mirrors pageNo state so the debounce callback can read the
  //              current page without a stale closure.
  const searchRef   = useRef("");
  const debounceRef = useRef(null);
  const pageNoRef   = useRef(1);

  useEffect(() => { pageNoRef.current = pageNo; }, [pageNo]);

  const { firm_name } = useSelector((state) => state.profile);
  const { user_id }   = useSelector((state) => state.other);

  useEffect(() => {
    const todayFormatted = moment().format("YYYY-MM-DD");
    setAddData({ from_date: todayFormatted, to_date: todayFormatted });
  }, []);

  // Re-fetch when the user changes page or entries-per-page.
  // The search term is read from searchRef so it is always current.
  useEffect(() => {
    if (showData) {
      get_list(addDataPre);
    }
  }, [pageNo, entries]);

  const table_header = [
    "Module Name",
    "Transaction Amount",
    "Transaction ID",
    "Transaction Date",
    "Party Name",
    "Created By",
    "Created Date",
    "Deleted By",
    "Deleted Date",
  ];

  // ── API call ──────────────────────────────────────────────────────────────
  const get_list = async (addData, data1 = "") => {
    const data = validate(addData);
    if (data?.length === 0) {
      try {
        setShowLoader(true);
        const body = new FormData();
        body.append("action",   "list");
        body.append("agencyid", user_id);
        body.append("fd",       addData?.from_date);
        body.append("td",       addData?.to_date);
        body.append("search",   searchRef.current);          // ← backend search
        body.append(
          "page",
          data1?.length > 1 || data1 === "main" ? 1 : pageNo,
        );
        body.append(
          "limit",
          data1?.length > 1
            ? data1 === "main"
              ? entries
              : totalCount
            : entries,
        );

        const response = await fetch(base_url + "reports/deleted_report.php", {
          method: "POST",
          body,
        });
        const result = await response.json();
        setShowLoader(false);

        if (result.success) {
          if (data1 === "excel") {
            export_excel(addData, result?.data);
          } else if (data1 === "pdf") {
            export_pdf(addData, result?.data);
          } else {
            setDeletedReportItemList(result?.data || []);
            setTotalCount(result?.totalcount);
            // searchcount is returned by the API only when a search term is
            // active; null means "no search — use totalcount for pagination".
            setSearchCount(result?.searchcount ?? null);
            setShowData(true);
            setAddDataPre(addData);
          }
        } else {
          setMess("No Data Found");
          setOpen(true);
        }
      } catch (error) {
        console.log(error);
        setMess("Error fetching deleted report");
        setOpen(true);
        setShowLoader(false);
      }
    } else {
      setMess(<Alerts data={data} />);
      setOpen(true);
    }
  };

  // ── Debounced search handler ───────────────────────────────────────────────
  // 1. Update the display input immediately (good UX).
  // 2. Update searchRef immediately so any concurrent get_list() call picks
  //    up the latest value.
  // 3. After 500 ms of inactivity, trigger an API fetch:
  //    • If currently on page > 1 → reset to page 1; the useEffect above will
  //      fire and call get_list() with the updated searchRef.
  //    • If already on page 1 → call get_list() directly (the effect won't
  //      fire because pageNo didn't change).
  const handleSearchChange = (val) => {
    setSerchInput(val);
    searchRef.current = val;

    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      if (!showData) return;            // no results loaded yet, nothing to search
      if (pageNoRef.current !== 1) {
        setPageNo(1);                   // triggers useEffect([pageNo, entries])
      } else {
        get_list(addDataPre);           // already on page 1 — fetch directly
      }
    }, 500);
  };

  const validate = (addData) => {
    const data = [];
    if (!addData?.from_date || addData?.from_date?.length !== 10) {
      data.push("Please Enter From Date");
    }
    if (!addData?.to_date || addData?.to_date?.length !== 10) {
      data.push("Please Enter To Date");
    }
    return data;
  };

  const export_pdf = (addData, data) => {
    const exportData = data?.map((v) => [
      v?.SOURCE         || "NA",
      v?.INVOICE_AMOUNT || "NA",
      v?.SR_NO          || "NA",
      v?.SR_DATE        || "NA",
      v?.PARTY_NAME     || "NA",
      v?.CREATED_NAME   || "NA",
      v?.CREATED_DATE   || "NA",
      v?.DELETED_NAME   || "NA",
      v?.DELETED_DATE   || "NA",
    ]);
    exportPDF({
      table_data:    exportData,
      table_headers: table_header,
      tableName: `${firm_name} - DELETED REPORT -- FROM DATE : ${moment(
        addData?.from_date,
      ).format("DD/MM/YY")} & TO DATE : ${moment(addData?.to_date).format(
        "DD/MM/YY",
      )}`,
      fileName: firm_name,
    });
  };

  const export_excel = (addData, data) => {
    const exportData = data?.map((v) => [
      v?.SOURCE         || "NA",
      v?.INVOICE_AMOUNT || "NA",
      v?.SR_NO          || "NA",
      v?.SR_DATE        || "NA",
      v?.PARTY_NAME     || "NA",
      v?.CREATED_NAME   || "NA",
      v?.CREATED_DATE   || "NA",
      v?.DELETED_NAME   || "NA",
      v?.DELETED_DATE   || "NA",
    ]);
    downloadExcel(
      exportData,
      table_header,
      `${firm_name} - DELETED REPORT -- FROM DATE : ${moment(
        addData?.from_date,
      ).format("DD/MM/YY")} & TO DATE : ${moment(addData?.to_date).format(
        "DD/MM/YY",
      )}`,
    );
  };

  // When a search is active the API returns `searchcount` (how many rows match
  // the search term). Use that for pagination so page numbers and visibility
  // are based on the filtered set, not the full date-range total.
  const effectiveTotalCount =
    serchInput && searchCount != null ? searchCount : totalCount;

  return (
    <div className="container">
      <PageHeading title={"DELETED RECORDS LOGS"} right={<BackButton />} />

      <div className="transactionMain">
        <div style={{ width: "180px" }}>
          <Input
            label={"FROM DATE"}
            type={"date"}
            value={addData?.from_date}
            onChange={(e) => {
              if (e.target.value.length <= 10) {
                setAddData({ ...addData, from_date: e.target.value });
              }
            }}
          />
        </div>
        <div style={{ width: "180px" }}>
          <Input
            label={"TO DATE"}
            value={addData?.to_date}
            onChange={(e) => {
              if (e.target.value.length <= 10) {
                setAddData({ ...addData, to_date: e.target.value });
              }
            }}
            type={"date"}
          />
        </div>

        <div style={{ display: "flex", alignItems: "flex-end" }}>
          <Button
            title={"SEARCH"}
            onClick={async () => {
              // Clear search when doing a fresh date-range search
              searchRef.current = "";
              setSerchInput("");
              setSearchCount(null);
              setShowData(false);
              setPageNo(1);
              setEntries(10);
              get_list(addData, "main");
            }}
          />
        </div>
      </div>
            
      {showData && deleted_report_item_list?.length > 0 ? (
        <>
          <div className="SFromDate"></div>

          <Pagination2
            count={0}
            download={true}
            PDFDown={() => get_list(addDataPre, "pdf")}
            excelDown={() => get_list(addDataPre, "excel")}
            top={true}
            totalCount={effectiveTotalCount}
            SerchInput={serchInput}
            setSearchInput={handleSearchChange}
            pageNo={pageNo}
            setPageNo={setPageNo}
            entries={entries}
            setEntries={setEntries}
          />

          <Table
            headData={table_header}
            body={
              <>
                {deleted_report_item_list?.map((v, i) => (
                  <tr key={i}>
                    <td>{v?.SOURCE         || "NA"}</td>
                    <td>{v?.INVOICE_AMOUNT || "NA"}</td>
                    <td>{v?.SR_NO          || "NA"}</td>
                    <td>{v?.SR_DATE        || "NA"}</td>
                    <td>{v?.PARTY_NAME     || "NA"}</td>
                    <td>{v?.CREATED_NAME   || "NA"}</td>
                    <td>{v?.CREATED_DATE   || "NA"}</td>
                    <td>{v?.DELETED_NAME   || "NA"}</td>
                    <td>{v?.DELETED_DATE   || "NA"}</td>
                  </tr>
                ))}
              </>
            }
          />

          <Pagination2
            count={0}
            bottom={true}
            totalCount={effectiveTotalCount}
            SerchInput={serchInput}
            setSearchInput={handleSearchChange}
            pageNo={pageNo}
            setPageNo={setPageNo}
            entries={entries}
            setEntries={setEntries}
          />
        </>
      ) : showData ? (
        <NoData title={"NO RECORDS FOUND"} color="red" mt={30} />
      ) : (
        ""
      )}

      <div style={{ height: "10px" }}></div>

      {open && (
        <SmallModal
          isOpen={open}
          setIsOpen={setOpen}
          title={"Alerts"}
          mess={mess}
          okbtn={setOpen}
        />
      )}
      {showLoader && <Loader />}
    </div>
  );
};

export default DeleteReport;
